import CircularProgress from '@mui/material/CircularProgress';
import { useEffect, useRef, useState } from 'react';

import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import googleLogo from 'assets/images/small-logos/logo-google.svg';

const GOOGLE_IDENTITY_SCRIPT_ID = 'matcha-google-identity-script';
const GOOGLE_BUTTON_MIN_HEIGHT = '49px';
const SOCIAL_BUTTON_RADIUS = '0.9rem';
const GOOGLE_WEB_CLIENT_ID =
  process.env.REACT_APP_WEB_GOOGLE_CLIENT_ID?.trim() ||
  process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim() ||
  '';

export const isGoogleSignInConfigured = Boolean(GOOGLE_WEB_CLIENT_ID);

interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleAccountsId {
  initialize: (options: {
    callback: (response: GoogleCredentialResponse) => void;
    client_id: string;
    context?: 'signin' | 'signup' | 'use';
    ux_mode?: 'popup' | 'redirect';
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      size?: 'large' | 'medium' | 'small';
      shape?: 'rectangular' | 'pill' | 'circle' | 'square';
      logo_alignment?: 'left' | 'center';
      text?: 'continue_with' | 'signin_with' | 'signup_with';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      type?: 'standard' | 'icon';
      width?: string;
    }
  ) => void;
}

interface GoogleIdentityWindow extends Window {
  google?: {
    accounts?: {
      id?: GoogleAccountsId;
    };
  };
}

interface GoogleSignInButtonProps {
  isSubmitting: boolean;
  onCredential: (idToken: string) => Promise<void>;
  onError: (message: string) => void;
  authFailedMessage: string;
  credentialMissingMessage: string;
  label: string;
  loadFailedMessage: string;
}

function getGoogleIdentityWindow(): GoogleIdentityWindow | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window as GoogleIdentityWindow;
}

function loadGoogleIdentityScript(): Promise<void> {
  const googleWindow = getGoogleIdentityWindow();
  if (!googleWindow) {
    return Promise.reject(new Error('Google Identity Services are unavailable.'));
  }

  if (googleWindow.google?.accounts?.id) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    let scriptElement = document.getElementById(
      GOOGLE_IDENTITY_SCRIPT_ID
    ) as HTMLScriptElement | null;

    const handleLoad = () => {
      cleanup();
      resolve();
    };

    const handleError = () => {
      cleanup();
      reject(new Error('Unable to load Google Identity Services.'));
    };

    const cleanup = () => {
      scriptElement?.removeEventListener('load', handleLoad);
      scriptElement?.removeEventListener('error', handleError);
    };

    if (!scriptElement) {
      scriptElement = document.createElement('script');
      scriptElement.id = GOOGLE_IDENTITY_SCRIPT_ID;
      scriptElement.src = 'https://accounts.google.com/gsi/client';
      scriptElement.async = true;
      scriptElement.defer = true;
      document.head.appendChild(scriptElement);
    }

    scriptElement.addEventListener('load', handleLoad);
    scriptElement.addEventListener('error', handleError);
  });
}

export function GoogleSignInButton({
  isSubmitting,
  onCredential,
  onError,
  authFailedMessage,
  credentialMissingMessage,
  label,
  loadFailedMessage,
}: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  const isInitializedRef = useRef(false);
  const isButtonRenderedRef = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);

  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!isGoogleSignInConfigured) {
      return;
    }

    let isActive = true;

    loadGoogleIdentityScript()
      .then(() => {
        if (!isActive) {
          return;
        }

        setIsReady(true);
        setHasLoadError(false);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }

        setHasLoadError(true);
        onErrorRef.current(loadFailedMessage);
      });

    return () => {
      isActive = false;
    };
  }, [loadFailedMessage]);

  useEffect(() => {
    const container = containerRef.current;
    const googleAccountsId = getGoogleIdentityWindow()?.google?.accounts?.id;

    if (!container || !isReady || !googleAccountsId) {
      return;
    }

    if (!isInitializedRef.current) {
      googleAccountsId.initialize({
        client_id: GOOGLE_WEB_CLIENT_ID,
        ux_mode: 'popup',
        context: 'signin',
        callback: async (response) => {
          const idToken = response.credential?.trim() ?? '';

          if (!idToken) {
            onErrorRef.current(credentialMissingMessage);
            return;
          }

          try {
            await onCredentialRef.current(idToken);
          } catch (error) {
            onErrorRef.current(
              error instanceof Error && error.message.trim()
                ? error.message
                : authFailedMessage
            );
          }
        },
      });
      isInitializedRef.current = true;
    }

    if (!isButtonRenderedRef.current) {
      container.innerHTML = '';
      googleAccountsId.renderButton(container, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        logo_alignment: 'center',
        text: 'continue_with',
        width: `${Math.round(container.getBoundingClientRect().width || 320)}`,
      });
      isButtonRenderedRef.current = true;
    }
  }, [authFailedMessage, credentialMissingMessage, isReady]);

  if (!isGoogleSignInConfigured || hasLoadError) {
    return null;
  }

  return (
    <SoftBox
      position="relative"
      minHeight={GOOGLE_BUTTON_MIN_HEIGHT}
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="100%"
      aria-busy={!isReady || isSubmitting}
      sx={{
        border: ({ palette }) => `1px solid ${palette.grey[300]}`,
        borderRadius: SOCIAL_BUTTON_RADIUS,
        backgroundColor: ({ palette }) => palette.common.white,
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
        overflow: 'hidden',
        transition: 'background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
        boxSizing: 'border-box',
        '&:hover': {
          backgroundColor: ({ palette }) =>
            isReady && !isSubmitting ? palette.grey[100] : palette.common.white,
          borderColor: ({ palette }) =>
            isReady && !isSubmitting ? palette.grey[400] : palette.grey[300],
          boxShadow:
            isReady && !isSubmitting
              ? '0 12px 28px rgba(15, 23, 42, 0.08)'
              : '0 10px 24px rgba(15, 23, 42, 0.06)',
        },
      }}
    >
      <SoftBox
        ref={containerRef}
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        minHeight={GOOGLE_BUTTON_MIN_HEIGHT}
        sx={{
          zIndex: 2,
          opacity: isReady && !isSubmitting ? 0.01 : 0,
          pointerEvents: isReady && !isSubmitting ? 'auto' : 'none',
          '& > div': {
            width: '100%',
            height: '100%',
          },
          '& iframe': {
            width: '100% !important',
            minWidth: '100% !important',
            height: `${GOOGLE_BUTTON_MIN_HEIGHT} !important`,
            minHeight: `${GOOGLE_BUTTON_MIN_HEIGHT} !important`,
          },
        }}
      />
      <SoftBox
        aria-hidden="true"
        px={2.25}
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={1.5}
        sx={{
          minHeight: '100%',
          pointerEvents: 'none',
          zIndex: 3,
          color: ({ palette }) => palette.text.primary,
        }}>
        {isSubmitting ? (
          <CircularProgress size={18} color="inherit" />
        ) : (
          <SoftBox
            component="img"
            src={googleLogo}
            alt=""
            aria-hidden="true"
            sx={{
              width: '18px',
              height: '18px',
              flexShrink: 0,
            }}
          />
        )}
        <SoftTypography
          fontWeight="medium"
          color="dark"
          sx={{
            fontSize: '0.95rem',
            lineHeight: 1.2,
            letterSpacing: '0.01em',
          }}>
          {label}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}
